import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = (() => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return window.location.origin + '/api';
  }
  return (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';
})();

export default function RoadOpsAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get('token');
    const error = searchParams.get('error');

    if (error) {
      const messages = {
        roadops_denied: 'RoadOps sign-in was cancelled.',
        token_exchange_failed: 'Unable to complete RoadOps sign-in. Please try again.',
        no_email: 'No email was returned from RoadOps. Make sure your RoadOps profile has a verified email.',
        invalid_token: 'RoadOps sign-in could not be verified. Please try again.',
        invalid_id_token: 'RoadOps sign-in could not be verified. Please try again.',
        invalid_state: 'RoadOps sign-in expired. Please try again.',
        no_token: 'RoadOps did not return a usable token. Please try again.',
        missing_id_token: 'RoadOps did not return an id_token. Please try again.',
      };
      navigate('/login', {
        replace: true,
        state: { message: messages[error] || 'RoadOps sign-in failed. Please try again.' },
      });
      return;
    }

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        localStorage.setItem('token', token);
        localStorage.setItem('keepLoggedIn', 'true');
        localStorage.setItem('loginExpiry', (Date.now() + 30 * 24 * 60 * 60 * 1000).toString());
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const me = res.data || {};
        localStorage.setItem('user', JSON.stringify(me));
        console.info(
          '[RoadOps Callback] /auth/me ok — role=' + me.role +
          ' profile_complete=' + me.profile_complete +
          ' email=' + me.email
        );
        // Route the user to the right place rather than always /dashboard.
        // New OAuth users land at /complete-profile until profile_complete=true.
        if (me.role === 'master_admin' || me.role === 'advertiser') {
          window.location.href = '/dashboard';
        } else if (me.profile_complete) {
          window.location.href = '/dashboard';
        } else {
          window.location.href = '/complete-profile';
        }
      } catch (err) {
        // Surface the real failure reason instead of silently bouncing.
        const detail = err?.response?.data?.detail
          || err?.response?.status
          || err?.message
          || 'unknown';
        console.error('RoadOps auth callback error:', err);
        localStorage.removeItem('token');
        navigate('/login', {
          replace: true,
          state: {
            message: `RoadOps sign-in failed at the final step (auth/me: ${detail}). `
              + `Open the browser console for more detail and screenshot it for support.`,
          },
        });
      }
    })();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-gray-300 text-sm">Signing in with RoadOps...</p>
      </div>
    </div>
  );
}
